import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Lock, CheckCircle } from 'lucide-react';
import { safeFetch } from '../utils/api';

import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [tokenError, setTokenError] = useState('');

  useEffect(() => {
    // Récupérer le token de l'URL
    const tokenParam = searchParams.get('token');
    
    if (!tokenParam) {
      setTokenError('Lien invalide : token manquant.');
      toast.error('❌ Lien de réinitialisation invalide.');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams]);

  const validatePassword = () => {
    if (newPassword.length < 8) {
      toast.error('❌ Le mot de passe doit contenir au moins 8 caractères.');
      return false;
    }

    if (newPassword !== confirmPassword) {
      toast.error('❌ Les mots de passe ne correspondent pas.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePassword()) {
      return;
    }

    setLoading(true);

    try {
      const response = await safeFetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: token,
          new_password: newPassword
        })
      });

      if (response.ok) {
        const data = await response.json();
        setResetSuccess(true);
        toast.success('✅ Ton mot de passe a été réinitialisé avec succès !');
        
        // Rediriger vers la page de connexion après 3 secondes
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        const error = await response.json();
        
        const errorMessages = {
          'TOKEN_EXPIRED': '❌ Ce lien a expiré. Demande un nouveau lien de réinitialisation.',
          'INVALID_TOKEN': '❌ Ce lien est invalide. Vérifie que tu as copié le lien complet.',
          'TOKEN_NOT_FOUND': '❌ Ce lien n\'existe pas ou a été révoqué.',
          'TOKEN_ALREADY_USED': '❌ Ce lien a déjà été utilisé. Demande un nouveau lien si nécessaire.',
          'USER_NOT_FOUND': '❌ Utilisateur introuvable.',
          'PASSWORD_UPDATE_FAILED': '❌ Erreur lors de la mise à jour du mot de passe.'
        };
        
        const message = errorMessages[error.detail] || '❌ Une erreur est survenue. Réessaye plus tard.';
        toast.error(message);
        setTokenError(error.detail);
      }
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error('❌ Erreur de connexion au serveur.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="w-full max-w-md">
        <Card className="shadow-xl border-2">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-2">
              {resetSuccess ? (
                <CheckCircle className="w-6 h-6 text-white" />
              ) : (
                <Lock className="w-6 h-6 text-white" />
              )}
            </div>
            <CardTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              {resetSuccess ? 'Succès !' : 'Nouveau mot de passe'}
            </CardTitle>
            <CardDescription>
              {resetSuccess
                ? 'Ton mot de passe a été modifié.'
                : tokenError
                ? 'Une erreur est survenue.'
                : 'Choisis un nouveau mot de passe sécurisé.'}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {resetSuccess ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-green-800">
                    ✅ Ton mot de passe a été réinitialisé avec succès !
                  </p>
                  <p className="text-xs text-green-700 mt-2">
                    Tu peux maintenant te connecter avec ton nouveau mot de passe.
                  </p>
                </div>

                <Button
                  onClick={() => navigate('/login')}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  Se connecter
                </Button>

                <p className="text-xs text-center text-gray-500">
                  Redirection automatique dans 3 secondes...
                </p>
              </div>
            ) : tokenError ? (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-red-800">
                    ❌ {tokenError === 'TOKEN_EXPIRED'
                      ? 'Ce lien a expiré.'
                      : tokenError === 'TOKEN_ALREADY_USED'
                      ? 'Ce lien a déjà été utilisé.'
                      : 'Ce lien est invalide.'}
                  </p>
                </div>

                <Button
                  onClick={() => navigate('/forgot-password')}
                  className="w-full"
                  variant="outline"
                >
                  Demander un nouveau lien
                </Button>

                <Button
                  onClick={() => navigate('/login')}
                  className="w-full"
                  variant="ghost"
                >
                  Retour à la connexion
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">Nouveau mot de passe</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Minimum 8 caractères"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    autoFocus
                  />
                  <p className="text-xs text-gray-500">
                    Minimum 8 caractères
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Retape ton mot de passe"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  disabled={loading}
                >
                  {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
                </Button>

                <p className="text-xs text-center text-gray-500 mt-4">
                  Tu te souviens de ton mot de passe ?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  >
                    Connecte-toi
                  </button>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
